import { memo, useEffect, useRef, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDaysIcon,
  ChevronRight,
  FileTextIcon,
  UsersIcon,
  WalletIcon,
} from "lucide-react";
import { ui, useRuntimeEnv, useUrlActive } from "@harea/component";
import { useSidebarNavigation } from "hook/useSidebarNavigation";
import type { NavItem } from "components/SidebarNavItem";

const {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  useSidebar,
} = ui;

const sidebarStyle = { display: "contents" } as CSSProperties;

const menuItemClassName = "!rounded-r-[18px] !rounded-l-none pr-2 pl-4";

const subItemClassName = "!rounded-r-[18px] pr-2 pl-3";

const fallbackIcons = [UsersIcon, CalendarDaysIcon, WalletIcon, FileTextIcon];

const MobileOpenSync = ({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle?: () => void;
}) => {
  const { isMobile, openMobile, setOpenMobile } = useSidebar();
  const previousOpen = useRef(open);
  const previousMobile = useRef(openMobile);

  useEffect(() => {
    if (isMobile && previousOpen.current !== open) {
      setOpenMobile(open);
    }
    previousOpen.current = open;
  }, [isMobile, open, setOpenMobile]);

  useEffect(() => {
    if (isMobile && previousMobile.current && !openMobile && open) {
      onToggle?.();
    }
    previousMobile.current = openMobile;
  }, [isMobile, openMobile, open, onToggle]);

  return null;
};

const SidebarNavigation = memo(function SidebarNavigation() {
  const navigation = useSidebarNavigation();
  const isActive = useUrlActive();

  const primaryGroups = navigation.filter((category) => !category.label);
  const sections = navigation.filter((category) => !!category.label);

  const flatItems = [
    ...primaryGroups.flatMap((group) => group.children ?? []),
    ...sections.flatMap((section) => section.children ?? []),
  ];

  const renderIconItem = (item: NavItem, index: number) => (
    <SidebarMenuItem key={`${item.label ?? "item"}-${index}`}>
      <SidebarMenuButton
        asChild
        tooltip={item.label}
        isActive={!!item.to && isActive(item.to)}
        className="justify-center group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!p-0 [&>svg]:!size-5 [&>i]:!text-lg"
      >
        <Link to={item.to ?? "#"}>{item.icon}</Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  const renderLeaf = (item: NavItem, index: number) => (
    <SidebarMenuItem key={`${item.label ?? "item"}-${index}`}>
      <SidebarMenuButton asChild tooltip={item.label} className={menuItemClassName}>
        <Link to={item.to ?? "#"}>
          {item.icon}
          <span>{item.label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  const renderSection = (section: NavItem, index: number) => {
    const items = (section.children ?? []).filter(
      (child) => child.to || (child.children && child.children.length > 0),
    );
    const sectionActive = items.some(
      (child) => !!child.to && isActive(child.to),
    );
    const FallbackIcon = fallbackIcons[index % fallbackIcons.length];

    return (
      <Collapsible
        key={`${section.label ?? "section"}-${index}`}
        asChild
        defaultOpen={sectionActive}
        className="group/collapsible"
      >
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              tooltip={section.label}
              className={menuItemClassName}
            >
              {section.icon ?? <FallbackIcon />}
              <span>{section.label}</span>
              <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {items.map((child) => (
                <SidebarMenuSubItem key={child.label}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={!!child.to && isActive(child.to)}
                    className={subItemClassName}
                  >
                    <Link to={child.to ?? "#"}>
                      {child.icon}
                      <span>{child.label}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  };

  return (
    <>
      <div className="hidden group-data-[collapsible=icon]:block">
        <SidebarGroup className="p-1">
          <SidebarGroupContent>
            <SidebarMenu className="gap-2">
              {flatItems.map(renderIconItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </div>

      <div className="flex flex-col gap-2 group-data-[collapsible=icon]:hidden">
        {primaryGroups.map((group, groupIndex) => (
          <SidebarGroup key={groupIndex} className="pr-4 pl-0">
            <SidebarGroupContent>
              <SidebarMenu className="gap-2">
                {(group.children ?? []).map(renderLeaf)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}

        <SidebarGroup className="pr-4 pl-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-2">{sections.map(renderSection)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </div>
    </>
  );
});

export const AdminLeftSideBar5 = ({ open, onToggle }: AdminLeftSideBar5Props) => {
  const { runtimeEnv } = useRuntimeEnv();

  const designation = runtimeEnv?.organisation?.designation ?? "Harea";

  return (
    <SidebarProvider
      open={open}
      onOpenChange={(next) => {
        if (next !== open) {
          onToggle?.();
        }
      }}
      style={sidebarStyle}
    >
      <MobileOpenSync open={open} onToggle={onToggle} />
      <Sidebar variant="sidebar" collapsible="icon">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                size="lg"
                asChild
                tooltip={designation}
                className="gap-2.5 bg-transparent hover:bg-transparent group-data-[collapsible=icon]:justify-center"
              >
                <Link to="/">
                  <div className="flex aspect-square size-8 items-center justify-center overflow-hidden rounded-lg">
                    <img
                      src="/assets/img/logo.png"
                      alt="Logo"
                      className="size-8 object-contain"
                    />
                  </div>
                  <span className="truncate text-xl font-semibold group-data-[collapsible=icon]:hidden">
                    {designation}
                  </span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent className="[-ms-overflow-style:none] [scrollbar-width:none] group-data-[collapsible=icon]:!overflow-y-auto [&::-webkit-scrollbar]:hidden">
          <SidebarNavigation />
        </SidebarContent>

        <SidebarFooter className="mt-auto px-3 py-3 text-center text-[11px] leading-tight text-sidebar-foreground/60 group-data-[collapsible=icon]:px-0">
          <div className="flex items-center justify-center gap-1 whitespace-nowrap">
            <span>i-ASA</span>
            <span className="group-data-[collapsible=icon]:hidden">
              © {new Date().getFullYear()}
            </span>
          </div>
        </SidebarFooter>
      </Sidebar>
    </SidebarProvider>
  );
};

export type AdminLeftSideBar5Props = {
  open: boolean;
  onToggle?: () => void;
};

export default AdminLeftSideBar5;
